// LOAD USERS
import { Address, School, SchoolUser, SyncMode, UserProfile } from "@core/models/domain/account.domain";
import { SchoolSyncStatusResponse } from "@core/models/response/account.response";
import { AddUserRequest, ModifyUserRequest, ModifyUserProfileRequest, ModifySchoolRequest, UpdateSchoolContactRequest, UpdateSchoolLogoRequest } from "@core/models/request/account.request";
import {createAction, props} from "@ngrx/store";

export const loadUsers = createAction('[Account] Load Users');
// Store-first variant: only triggers loadUsers when the cache is empty, stale or invalidated.
export const loadUsersIfNeeded = createAction('[Account] Load Users If Needed');
export const loadUsersSuccess = createAction(
    '[Account] Load Users Success',
    props<{ users: SchoolUser[] }>()
);
export const loadUsersFailure = createAction(
    '[Account] Load Users Failure',
    props<{ error: any }>()
);

// GET AN INDIVIDUAL USER
// export const getUser = createAction(
//     '[Account] Get Individual User',
//     props<{ userAccountId: string }>()
// );
// export const getUserSuccess = createAction(
//     '[Account] Get Individual User Success',
//     props<{ user: SchoolUser }>()
// );
// export const getUserFailure = createAction(
//     '[Account] Get Individual User Failure',
//     props<{ error: any }>()
// );

// ADD USER
export const addUser = createAction(
    '[Account] Add User',
    props<{ request: AddUserRequest }>()
);
export const addUserSuccess = createAction(
    '[Account] Add User Success',
    props<{ user: SchoolUser }>()
);
export const addUserFailure = createAction(
    '[Account] Add User Failure',
    props<{ error: any }>()
);

// UPDATE USER
export const updateUser = createAction(
    '[Account] Update User',
    props<{ request: ModifyUserRequest }>()
);
export const updateUserSuccess = createAction(
    '[Account] Update User Success',
    props<{ user: SchoolUser }>()
);
export const updateUserFailure = createAction(
    '[Account] Update User Failure',
    props<{ error: any }>()
);

// REMOVE USER
export const removeUser = createAction(
    '[Account] Remove User',
    props<{ userAccountId: string }>()
);
export const removeUserSuccess = createAction(
    '[Account] Remove User Success',
    props<{ userAccountId: string }>()
);
export const removeUserFailure = createAction(
    '[Account] Remove User Failure',
    props<{ error: any }>()
);

// UPDATE USER PROFILE
export const updateUserProfile = createAction(
    '[Account] Update User profile',
    props<{ request: ModifyUserProfileRequest }>()
);
export const updateUserProfileSuccess = createAction(
    '[Account] Update User profile Success',
    props<{ userProfile: UserProfile }>()
);
export const updateUserProfileFailure = createAction(
    '[Account] Update User profile Failure',
    props<{ error: any }>()
);

export const loadSchoolInfo = createAction(
    '[Account] Load School Info',
    props<{ schoolAccountId: string }>()
);
export const loadSchoolInfoSuccess = createAction(
    '[Account] Load School Info Success',
    props<{ schoolInfo: School }>()
);
export const loadSchoolInfoFailure = createAction(
    '[Account] Load School Info Failure',
    props<{ error: any }>()
);

/** {@code confirmLargeRemovals}: the admin pressed Sync anyway after a run stopped on the removal share. */
export const syncSchoolDataFromIntegration = createAction(
    '[Account] Sync School Data From Integration',
    props<{ schoolId: string; schoolAccountId: string; confirmLargeRemovals?: boolean; syncMode?: SyncMode }>()
);
/**
 * The backend accepted the run. It keeps going server-side, so from here on the page follows it
 * through the status below rather than through the response of the call that started it.
 */
export const syncSchoolDataFromIntegrationStarted = createAction(
    '[Account] Sync School Data From Integration Started',
    props<{ schoolAccountId: string }>()
);
export const syncSchoolDataFromIntegrationFailure = createAction(
    '[Account] Sync School Data From Integration Failure',
    props<{ error: any }>()
);

// SYNC STATUS: read on entering the page, and polled while a run is in progress, so leaving the
// page or closing the browser never loses a run.
export const loadSyncStatus = createAction(
    '[Account] Load Sync Status',
    props<{ schoolAccountId: string }>()
);
export const loadSyncStatusSuccess = createAction(
    '[Account] Load Sync Status Success',
    props<{ schoolAccountId: string; status: SchoolSyncStatusResponse }>()
);
export const loadSyncStatusFailure = createAction(
    '[Account] Load Sync Status Failure',
    props<{ error: any }>()
);
/** The moment a run ended: everything a sync rewrites server-side is stale from here. */
export const syncRunFinished = createAction(
    '[Account] Sync Run Finished',
    props<{ schoolAccountId: string }>()
);

// SYNC MODE: how the MIS sync uses the school's data (DIGI-1851)
export const setSyncMode = createAction(
    '[Account] Set Sync Mode',
    props<{ schoolAccountId: string; syncMode: SyncMode }>()
);
export const setSyncModeSuccess = createAction(
    '[Account] Set Sync Mode Success',
    props<{ syncMode: SyncMode }>()
);
export const setSyncModeFailure = createAction(
    '[Account] Set Sync Mode Failure',
    props<{ error: string }>()
);

// UPDATE SCHOOL (rename)
export const updateSchool = createAction(
    '[Account] Update School',
    props<{ request: ModifySchoolRequest }>()
);
export const updateSchoolSuccess = createAction(
    '[Account] Update School Success',
    props<{ name: string }>()
);
export const updateSchoolFailure = createAction(
    '[Account] Update School Failure',
    props<{ error: any }>()
);

// UPDATE SCHOOL CONTACT (phone numbers + addresses)
export const updateSchoolContact = createAction(
    '[Account] Update School Contact',
    props<{ request: UpdateSchoolContactRequest }>()
);
export const updateSchoolContactSuccess = createAction(
    '[Account] Update School Contact Success',
    props<{ phoneNumbers: string[], notificationRecipientIds?: string[], address: Address | undefined }>()
);
export const updateSchoolContactFailure = createAction(
    '[Account] Update School Contact Failure',
    props<{ error: any }>()
);

export const updateSchoolLogo = createAction(
    '[Account] Update School Logo',
    props<{ request: UpdateSchoolLogoRequest }>()
);
export const updateSchoolLogoSuccess = createAction(
    '[Account] Update School Logo Success',
    props<{ logo: string }>()
);
export const updateSchoolLogoFailure = createAction(
    '[Account] Update School Logo Failure',
    props<{ error: string }>()
);

// DEACTIVATE SCHOOL (DIGI-1855): the school leaves LOQT; on success the admin is signed out.
export const deactivateSchool = createAction(
    '[Account] Deactivate School',
    props<{ confirmSchoolName: string }>()
);
export const deactivateSchoolSuccess = createAction('[Account] Deactivate School Success');
export const deactivateSchoolFailure = createAction(
    '[Account] Deactivate School Failure',
    props<{ error: string }>()
);
// A refusal shown in the Settings section is not shown again on a later visit.
export const clearDeactivateSchoolError = createAction('[Account] Clear Deactivate School Error');
